import { app } from "js/namespaces.js";
import * as geom from "js/core/utilities/geom";
import { BackgroundStyleType, HorizontalAlignType, TrayType } from "common/constants";
import { _ } from "js/vendor";
import { BaseElement } from "js/elements/base/BaseElement";
import { CollectionElement, CollectionItemElement } from "js/elements/base/CollectionElement";
import { MigrateTextToHTML, TextElement } from "js/elements/base/TextElement";
import { ContentElement, LogoElement } from "js/elements/base/ContentElement";
import { TextGroup } from "js/elements/base/TextGroup";
import { DisplayText } from "js/elements/elements/DisplayText";
import { AssetType, PaletteColorType, PositionType, VerticalAlignType } from "common/constants";
import { layoutHelper } from "../layouts/LayoutHelper";
import { migrateTextModel } from "js/elements/base/TextElement";
import { TextBlockPreset } from "common/constants";
import { ContentBlockContainer } from "js/elements/elements/ContentBlock";
import { getValueOrDefault } from "js/core/utilities/extensions";

class PresentationTitle extends BaseElement {
    getCanvasMargins() {
        return { left: 0, right: 0, top: 0, bottom: 0 };
    }

    get titleDecoration() {
        if (this.model.decoration) {
            return this.model.decoration;
        } else {
            // use theme decoration to get default title decoration
            switch (app.currentTheme.get("styleDecoration")) {
                case "bar_left":
                    return "left_border";
                case "bar_top_center":
                case "bar_top_left":
                    return "top_border";
                case "border":
                    return "frame";
                case "block":
                case "block_left":
                    return "block";
                case "underline_left":
                case "underline_center":
                    return "underline";
                default:
                    return "none";
            }
        }
    }

    get canRollover() {
        return true;
    }

    registerAnimations(animationManager) {
        animationManager.showAnimationProperties = false;
    }

    get showPresenters() {
        return getValueOrDefault(this.model.showPresenters && this.model.presenters && this.model.presenters.items && this.model.presenters.items.length, false);
    }

    get showFooter() {
        return this.model.showFooter;
    }

    get showLogos() {
        return getValueOrDefault(this.model.showLogos && this.model.logos && this.model.logos.items && this.model.logos.items.length, false);
    }

    get showImage() {
        return this.model.image != null && !_.isEmpty(this.model.image.position);
    }

    get canStyleText() {
        return this.showImage && this.imagePosition == "background";
    }

    get imagePosition() {
        return this.model.image.position || "left";
    }

    get imageTraySize() {
        return this.model.image.traySize || 400;
    }

    get textPosition() {
        if (this.model.textPosition) {
            return this.model.textPosition;
        } else {
            // use theme decoration to get default text position
            if (app.currentTheme.get("styleDecoration").contains("center")) {
                return "center";
            } else {
                return "left";
            }
        }
    }

    get hasTopBar() {
        return this.showLogos && (this.logos.position == PositionType.TOP_LEFT || this.logos.position() == PositionType.TOP_RIGHT);
    }

    get hasBottomBar() {
        return (this.showLogos && (this.logos.position == PositionType.BOTTOM_LEFT || this.logos.position() == PositionType.BOTTOM_RIGHT)) || this.showPresenters;
    }

    _build() {
        if (this.showImage) {
            this.image = this.addElement("image", () => PresentationTitleImage, { model: this.model.image });
            this.image.layer = -1;
        }

        this.displayText = this.addElement("displayText", () => PresentationTitleDisplayText, {
            singleLineHeadline: true
        });

        if (this.showPresenters) {
            this.presenters = this.addElement("presenters", () => PresentationTitlePresenters, { model: this.model.presenters });
        }
        if (this.showFooter) {
            this.footer = this.addElement("footer", () => PresentationTitleFooter);
        }
        if (this.showLogos) {
            this.logos = this.addElement("logos", () => PresentationLogos, { model: this.model.logos });
            this.logos.layer = 1;
        }
    }

    _loadStyles(styles) {
        if (this.titleDecoration && this.titleDecoration != "none") {
            let decoration = this.titleDecoration;

            if (decoration == "underline") {
                switch (this.textPosition) {
                    case "left":
                    case "top_left":
                    case "bottom_left":
                        decoration = "underline_left";
                        break;
                    case "right":
                    case "top_right":
                    case "bottom_right":
                        decoration = "underline_right";
                        break;
                    default:
                        decoration = "underline_center";
                }
            }

            styles.applyStyles(styles.decorations[decoration]);
        }
    }

    _calcProps(props, options) {
        let { size, children, styles } = props;

        let availableBounds = new geom.Rect(0, 0, size);

        switch (this.titleDecoration) {
            case "top_border":
                availableBounds = availableBounds.deflate({ top: app.currentTheme.styleSheet.$decorationStrokeWidth * 3 });
                break;
            case "left_border":
                availableBounds = availableBounds.deflate({ left: app.currentTheme.styleSheet.$decorationStrokeWidth * 3 });
                break;
            case "frame":
                availableBounds = availableBounds.deflate(app.currentTheme.styleSheet.$decorationStrokeWidth * 4);
                break;
        }

        if (this.model.showFooter) {
            let footerProps = children.setProps(this.footer, size);
            footerProps.bounds = new geom.Rect(0, size.height - footerProps.size.height, size.width, footerProps.size.height);
            availableBounds = availableBounds.deflate({ bottom: footerProps.size.height });
        }

        let logosProps;
        if (this.showLogos) {
            logosProps = children.setProps(this.logos, size);
            switch (this.logos.position) {
                case PositionType.CENTER:
                    // handled below after text layout
                    break;
                case PositionType.TOP_RIGHT:
                    logosProps.bounds = new geom.Rect(availableBounds.right - logosProps.size.width, availableBounds.top, logosProps.size);
                    break;
                case PositionType.TOP_LEFT:
                default:
                    logosProps.bounds = new geom.Rect(availableBounds.left, availableBounds.top, logosProps.size);
            }
        }

        if (this.showImage) {
            let imageProps;
            switch (this.imagePosition) {
                case "left":
                    imageProps = children.setProps(this.image, new geom.Size(this.imageTraySize, availableBounds.height));
                    imageProps.bounds = new geom.Rect(availableBounds.left, availableBounds.top, imageProps.size);
                    availableBounds = availableBounds.deflate({ left: imageProps.size.width });
                    break;
                case "right":
                    imageProps = children.setProps(this.image, new geom.Size(this.imageTraySize, availableBounds.height));
                    imageProps.bounds = new geom.Rect(availableBounds.right - imageProps.size.width, availableBounds.top, imageProps.size);
                    availableBounds = availableBounds.deflate({ right: imageProps.size.width });
                    break;
                case "top":
                    imageProps = children.setProps(this.image, new geom.Size(availableBounds.width, this.imageTraySize));
                    imageProps.bounds = new geom.Rect(availableBounds.left, availableBounds.top, imageProps.size);
                    availableBounds = availableBounds.deflate({ top: imageProps.size.height });
                    break;
                case "bottom":
                    imageProps = children.setProps(this.image, new geom.Size(availableBounds.width, this.imageTraySize));
                    imageProps.bounds = new geom.Rect(availableBounds.left, availableBounds.bottom - imageProps.size.height, imageProps.size);
                    availableBounds = availableBounds.deflate({ bottom: imageProps.size.height });
                    break;
                case "background":
                    imageProps = children.setProps(this.image, availableBounds.size);
                    imageProps.bounds = new geom.Rect(availableBounds.left, availableBounds.top, imageProps.size);
                    break;
            }
        }

        let presenterProps;
        if (this.showPresenters) {
            if (this.textPosition.contains("left")) {
                this.presenters.styles.paddingLeft = this.displayText.styles.paddingLeft;
            } else {
                this.presenters.styles.paddingLeft = 30;
            }

            presenterProps = children.setProps(this.presenters, availableBounds.size);
            presenterProps.bounds = new geom.Rect(availableBounds.left, availableBounds.bottom - presenterProps.size.height, availableBounds.width, presenterProps.size.height);
        }

        let textAreaBounds = availableBounds.clone();
        if (this.showLogos && !(this.showImage && this.imagePosition == "top") && this.textPosition.contains("top")) {
            textAreaBounds = textAreaBounds.deflate({ top: logosProps.size.height });
        }

        if (this.showPresenters && (this.textPosition.contains("bottom") || (this.showImage && this.imagePosition == "top"))) {
            textAreaBounds = textAreaBounds.deflate({ bottom: presenterProps.size.height });
        }

        if (this.displayText.hasBackdrop) {
            textAreaBounds = textAreaBounds.deflate({
                top: this.styles.titlePaddingVertical,
                bottom: this.styles.titlePaddingVertical,
                left: this.styles.titlePaddingHorizontal,
                right: this.styles.titlePaddingHorizontal
            });
        }

        switch (this.textPosition) {
            case "left":
            case "top_left":
            case "bottom_left":
                this.displayText.styles.textAlign = HorizontalAlignType.LEFT;
                // if (props.styles.displayText.textStyle.contains("box")){
                //     textAreaBounds = textAreaBounds.deflate({left: props.styles.displayText.styles.backdrop.paddingLeft});
                // }
                break;
            case "top":
            case "center":
            case "bottom":
                this.displayText.styles.textAlign = HorizontalAlignType.CENTER;
                break;
            case "top_right":
            case "right":
            case "bottom_right":
                this.displayText.styles.textAlign = HorizontalAlignType.RIGHT;
                break;
        }

        let textProps = children.setProps(this.displayText, textAreaBounds.size);
        textProps.bounds = new geom.Rect(textAreaBounds.left, textAreaBounds.centerV - textProps.size.height / 2, textProps.size);

        switch (this.textPosition) {
            case "left":
            case "right":
            case "center":
                textProps.bounds.top = textAreaBounds.centerV - textProps.size.height / 2;
                break;
            case "top_left":
            case "top_right":
            case "top":
                textProps.bounds.top = textAreaBounds.top + textProps.styles.backdrop.paddingTop;
                break;
            case "bottom_left":
            case "bottom_right":
            case "bottom":
                textProps.bounds.top = textAreaBounds.bottom - textProps.size.height - textProps.styles.backdrop.paddingBottom;
                break;
        }

        if (this.showLogos && this.logos.position == PositionType.CENTER) {
            let left;
            switch (this.textPosition) {
                case "left":
                case "top_left":
                case "bottom_left":
                    left = availableBounds.left;
                    break;
                case "top_right":
                case "bottom_right":
                case "right":
                    left = availableBounds.right - logosProps.size.width;
                    break;
                case "bottom":
                case "center":
                case "top":
                default:
                    left = availableBounds.left + layoutHelper.getHorizontalAlignOffset(logosProps.size.width, availableBounds.width, HorizontalAlignType.CENTER);
            }
            logosProps.bounds = new geom.Rect(left, textProps.bounds.top / 2 - logosProps.size.height / 2, logosProps.size);
        }

        return { size };
    }

    getBackgroundColor() {
        if (this.showImage && this.image && this.imagePosition == "background") {
            return BackgroundStyleType.IMAGE;
        } else {
            return super.getBackgroundColor();
        }
    }

    _migrate_8() {
        if (this.model.logos && this.model.logos.items && this.model.logos.items.length) {
            this.model.showLogos = true;
        }
        if (this.model.presenters && this.model.presenters.items && this.model.presenters.items.length) {
            this.model.showPresenters = true;
        }
    }
}

class PresentationTitleDisplayText extends DisplayText {
    get _canSelect() {
        return true;
    }

    get textPosition() {
        return this.parentElement.textPosition;
    }

    get isOverImage() {
        return this.parentElement.showImage && this.parentElement.imagePosition == "background";
    }

    get selectionPadding() {
        return 20;
    }

    get canPosition() {
        return false;
    }

    get backdropStyle() {
        // override the default backdrop style
        return this.model.backdrop || "white_box";
    }

    getBackgroundColor(forElement) {
        if (forElement && forElement instanceof TextElement && this.parentElement.titleDecoration == "block") {
            return this.getDecorationFillColor();
        } else {
            return super.getBackgroundColor(forElement);
        }
    }
}

class PresentationTitleImage extends ContentElement {
    get _canSelect() {
        return true;
    }
}

class PresentationLogos extends CollectionElement {
    static get schema() {
        return {
            scale: 0.5
        };
    }

    get _canSelect() {
        return true;
    }

    getChildItemType() {
        return PresentationLogoItem;
    }

    get position() {
        return this.model.position || "top-left";
    }

    get minItemCount() {
        return 0;
    }

    get selectionPadding() {
        return 0;
    }

    get scale() {
        return getValueOrDefault(this.model.scale, 1);
    }

    _migrate_8() {
        if (!this.model.scale) {
            this.model.scale = 0.5;
        }
    }

    _calcProps(props, options) {
        let { size, children, styles } = props;
        if (this.position == "center") {
            styles.applyStyles(styles.center);
        }

        let layouter = this.getLayouter(this.itemElements, new geom.Size(size.width, styles.logoHeight * this.scale));
        layouter.distributeHorizontally({
            gap: styles.hGap * this.scale,
            verticalAlign: VerticalAlignType.MIDDLE,
            useCalculatedSizes: false,
            itemOptions: {
                scale: this.scale
            }
        });

        //props.isFit = this.itemElements.reduce((acc, item) => acc + item.bounds.width + styles.hGap, 0) - styles.hGap < size.width;

        return { size: layouter.size };  // center position returns actual width of logos and not full width like the other positions
    }
}

class PresentationLogoItem extends CollectionItemElement {
    get _canSelect() {
        return true;
    }

    get name() {
        return "Logo";
    }

    get selectionPadding() {
        return 0;
    }

    _build() {
        this.logo = this.addElement("logo", () => LogoElement, {
            defaultAssetType: AssetType.LOGO,
            fitHeight: true
        });
    }

    get buildAnimations() {
        return [];
    }

    _calcProps(props, options) {
        let { size, children, styles } = props;

        this.logo.styles.paddingLeft = this.logo.styles.paddingRight = this.logo.styles.paddingTop = this.logo.styles.paddingBottom = 15 * options.scale;

        let logoProps = children.setProps(this.logo, size, { fitHeight: true });
        logoProps.bounds = new geom.Rect(0, 0, logoProps.size);

        return { size: logoProps.size };
    }
}

class PresentationTitlePresenters extends CollectionElement {
    get _canSelect() {
        return true;
    }

    get passThroughSelection() {
        return false;
    }

    getChildItemType() {
        return PresentationTitlePresenterItem;
    }

    get minItemCount() {
        return 0;
    }

    get layer() {
        return 2;
    }

    get showRollover() {
        return true;
    }

    get horizontalAlign() {
        return this.model.horizontalAlign || HorizontalAlignType.LEFT;
    }

    get isOverImage() {
        return this.parentElement.showImage && this.parentElement.imagePosition == "background";
    }

    get textStyle() {
        if (this.isOverImage) {
            return this.model.textStyle || "light";
        } else {
            return null;
        }
    }

    _loadStyles(styles) {
        switch (this.textStyle) {
            case "transparent_light_box":
                styles.decoration = {
                    type: "frame",
                    shape: "rect",
                    fillColor: "white",
                    fillOpacity: 0.5
                };
                break;
            case "white_box":
                styles.decoration = {
                    type: "frame",
                    shape: "rect",
                    fillColor: "white",
                    fillOpacity: 1
                };
                break;
            case "transparent_dark_box":
                styles.decoration = {
                    type: "frame",
                    shape: "rect",
                    fillColor: "black",
                    fillOpacity: 0.3
                };
                break;
            case "white_text":
                styles.PresentationTitlePresenterItem.text.title.fontColor = "primary_light";
                styles.PresentationTitlePresenterItem.text.body.fontColor = "primary_light";
                break;
            case "dark_text":
                styles.PresentationTitlePresenterItem.text.title.fontColor = "primary_dark";
                styles.PresentationTitlePresenterItem.text.body.fontColor = "primary_dark";
                break;
            case "white_text_with_shadow":
                styles.PresentationTitlePresenterItem.text.title.fontColor = "primary_light";
                styles.PresentationTitlePresenterItem.text.body.fontColor = "primary_light";
                styles.PresentationTitlePresenterItem.text.title.filter = "textShadow";
                styles.PresentationTitlePresenterItem.text.body.filter = "textShadow";
                break;
        }
    }

    _calcProps(props, options) {
        let { size, children, styles } = props;
        if (this.canvas.showBranding() && this.horizontalAlign == HorizontalAlignType.RIGHT && !this.parentElement.showFooter) {
            size.width -= 150;
        }

        let layouter = this.getLayouter(this.itemElements, size);
        layouter.distributeHorizontally({ gap: this.styles.hGap }).alignHorizontally(this.horizontalAlign);
        props.isFit = layouter.isFit;

        return { size: new geom.Size(size.width, layouter.size.height) };
    }
}

class PresentationTitlePresenterItem extends CollectionItemElement {
    get name() {
        return "Presenter";
    }

    get selectionPadding() {
        return 10;
    }

    _build() {
        this.text = this.addElement("text", () => TextGroup, {
            model: this.model,
            title: {
                singleLine: true,
                placeholder: "Type Presenter Name"
            },
            autoWidth: true,
            autoHeight: true
        });
    }

    _calcProps(props, options) {
        let { size, children, styles } = props;

        let textProps = children.setProps(this.text, size);

        return { size: textProps.size };
    }

    get buildAnimations() {
        return [];
    }
}

class PresentationTitleFooter extends BaseElement {
    _build() {
        this.info = this.addElement("info", () => TextElement, {
            singleLine: true,
            canStyle: true,
            autoHeight: true,
            textAlign: HorizontalAlignType.LEFT
        });

        this.disclaimer = this.addElement("disclaimer", () => TextElement, {
            singleLine: true,
            canStyle: true,
            autoHeight: true,
            textAlign: HorizontalAlignType.RIGHT
        });
    }

    _calcProps(props, options) {
        let { size, children, styles } = props;

        let infoProps = children.setProps(this.info, new geom.Size(size.width / 2, size.height));
        infoProps.bounds = new geom.Rect(0, 0, size.width / 2, infoProps.size.height);

        let disclaimerWidth = size.width / 2;
        if (this.canvas.showBranding()) {
            disclaimerWidth -= 150;
        }
        let disclaimerProps = children.setProps(this.disclaimer, new geom.Size(disclaimerWidth, size.height));
        disclaimerProps.bounds = new geom.Rect(size.width / 2, 0, disclaimerProps.size);

        return { size: new geom.Size(size.width, Math.max(infoProps.size.height, disclaimerProps.size.height)) };
    }
}

export { PresentationTitle };

export const elements = {
    PresentationTitle
};
